import moment from 'moment-timezone';
import _ from 'underscore';

import {
  getStopDocumentsConfig,
  getCombinedStopDocumentsConfig,
} from 'utils/UserServiceAPIUtils';
import { isDrayageShipment, isIntermodalShipmentType } from './shipment-types';
import { isActiveIntermodalShipment } from './order-shipments';
import { CONFIGURED_FEATURES, featureEnabled } from 'helpers/feature-flags';
import { STOP_STATUS } from 'constants/StopStatus';

export function isHazMat(job) {
  return job.stops.some((stop) => stop.hazards && stop.hazards.length > 0);
}

export function getTimezone(stop) {
  const { location } = stop;

  if (!location || location.length === 0) {
    return moment.tz.guess();
  }
  const stopLocation = location[0];
  if (stopLocation.location_timezone) {
    return stopLocation.location_timezone;
  }

  if (stopLocation.market && stopLocation.market.timezone) {
    return stopLocation.market.timezone;
  }

  return moment.tz.guess();
}

function createIntentFilter(action, empty) {
  return function (stop) {
    if (typeof empty === 'undefined') {
      return stop.action === action;
    }

    return stop.action === action && stop.empty === empty;
  };
}

function getStopByIntent(job, action, empty) {
  const { stops } = job;

  if (stops.length === 0) {
    return null;
  }

  const sortedStops = [...stops].sort((a, b) => a.sequence - b.sequence);

  return sortedStops.find(createIntentFilter(action, empty));
}

export function getNonEmptyStop(job, intent) {
  const stop = getStopByIntent(job, intent, false);

  if (stop) {
    return stop;
  }

  return getStopByIntent(job, intent);
}

export function getPickupStop(job) {
  return getNonEmptyStop(job, 'pickup');
}

export function getDeliverStop(job) {
  return getNonEmptyStop(job, 'deliver');
}

const optional = {
  pocOptional: true,
  dateRequired: false,
};

const defaultRequirements = {
  pocOptional: false,
  dateRequired: true,
};

export function getIntentRequirements(shipment, stop, isAdmin = false) {
  const drayageShipment = isIntermodalShipmentType(shipment);

  if (!stop) {
    return defaultRequirements;
  }

  // legacy action return is still handled
  if (stop.action === 'pickup' || stop.action === 'return') {
    return optional;
  }

  // For provide documentation workflow
  if (
    !stop.empty &&
    stop.action === 'deliver' &&
    (drayageShipment || isAdmin)
  ) {
    return {
      ...defaultRequirements,
      pocOptional: true,
    };
  }

  if (stop.empty && stop.action === 'deliver') {
    return {
      ...defaultRequirements,
      pocOptional: true,
    };
  }

  return defaultRequirements;
}

export const getDefaultMetaState = () => ({
  signatory: '',
  date: moment(),
  value: '',
  name: '',
  reasonCode: '',
  reasonDescription: '',
});

export function isAppointmentWindowEditable(type, job) {
  if (!isActiveIntermodalShipment(job)) {
    return false;
  }

  let stop;

  if (type === 'pickup') {
    stop = getPickupStop(job);
  } else {
    stop = getDeliverStop(job);
  }

  if (!stop) {
    return false;
  }

  return !stop.complete;
}

const getStop = (stops, findIndexFunction, fallbackFunction) => {
  let desiredStop;
  const sortedStops = _.sortBy(stops, 'sequence');
  const loadedIndex = findIndexFunction(sortedStops, (s) => {
    return !s.empty;
  });
  if (loadedIndex > -1) desiredStop = sortedStops[loadedIndex];
  else desiredStop = fallbackFunction(sortedStops || []);

  return desiredStop || null;
};

// Get first and last non-empty stops, regardless of intent.
// Fall back to actual first and last if they're empty.
export const getFirstStop = (stops) => {
  return getStop(stops, _.findIndex, _.first);
};

export const getLastStop = (stops) => {
  return getStop(stops, _.findLastIndex, _.last);
};

export const isStopMissingAppointment = (stop) => {
  return (
    !stop.empty &&
    (!stop.window_start || !stop.window_end || stop.appointment_unconfirmed)
  );
};

export const findNextStop = (stops) => {
  return _.find(
    _.sortBy(stops, (s) => {
      return s.sequence;
    }),
    (s) => {
      return !s.complete;
    },
  );
};

export function getActionDate(stop) {
  const {
    window_end,
    window_start,
    timeline: { complete },
  } = stop;
  const timezone = getTimezone(stop);

  if (complete && complete.meta && complete.meta.date) {
    return { date: moment(complete.meta.date).tz(timezone), timezone };
  }

  if (!window_end) {
    return { date: moment().tz(timezone), timezone };
  }

  const windowEnd = moment(window_end);

  if (moment().isBefore(window_start)) {
    return { date: moment().tz(timezone), timezone };
  }

  if (window_start && window_end === window_start) {
    return { date: windowEnd.tz(timezone), timezone };
  }
  return { date: windowEnd.subtract(5, 'minutes').tz(timezone), timezone };
}

export function stopRequiresChassis({ shipment, isCarrier }) {
  if (isCarrier) {
    return true;
  }

  const { shipper } = shipment;
  const tags = (shipper || {}).tags || [];

  return tags.includes('require_chassis_at_stop_completion');
}

export function stopRequiresTrailer({ shipment }) {
  const { shipper } = shipment;
  const tags = (shipper || {}).tags || [];

  return tags.includes('drayage_trailer_moves');
}

export function isChassisMissing({ shipment, equipments }) {
  if (stopRequiresTrailer({ shipment })) {
    return false;
  }
  if (!isDrayageShipment(shipment)) {
    return false;
  }
  const stopEquipments = equipments || [];
  if (stopEquipments.length === 0) {
    return false;
  }

  const hasContainer = stopEquipments.some((eq) => {
    return eq._equipmentType === 'container';
  });
  const hasChassis = stopEquipments.some((eq) => {
    return eq._equipmentType === 'chassis';
  });

  return hasContainer && !hasChassis;
}

export const getPickupEmptyStops = (stops = []) => {
  return stops.filter((s) => s.action === 'pickup' && s.empty && !s.off_site);
};

export const getPickupStops = (stops = []) => {
  return stops.filter((s) => s.action === 'pickup' && !s.empty && !s.off_site);
};

export const getOffsitePickupStops = (stops = []) => {
  return stops.filter((s) => s.action === 'pickup' && !s.empty && s.off_site);
};

export const getOffsitePickupEmptyStops = (stops = []) => {
  return stops.filter((s) => s.action === 'pickup' && s.empty && s.off_site);
};

export const getDeliverEmpty = (stops = []) => {
  return stops.filter((s) => s.action === 'deliver' && s.empty && !s.off_site);
};

export const getDeliverStops = (stops = []) => {
  return stops.filter((s) => s.action === 'deliver' && !s.empty && !s.off_site);
};

export const getOffsiteDeliverStops = (stops = []) => {
  return stops.filter((s) => s.action === 'deliver' && !s.empty && s.off_site);
};

export const getOffsiteDeliverEmptyStops = (stops = []) => {
  return stops.filter((s) => s.action === 'deliver' && s.empty && s.off_site);
};

export const getUniqueCombinedStopDocumentRequirementsFromConfig = async (
  shipper,
  fetchUniqueDocuments = true,
) => {
  try {
    const { data } = await getCombinedStopDocumentsConfig(shipper);

    return (
      data?.getCombinedStopDocumentsConfigQuery?.stopConfigurations || []
    ).reduce((lookup, currentStopConfig) => {
      const {
        actionType,
        empty,
        movementType,
        offsite,
        shipmentType,
        stopDocuments,
      } = currentStopConfig;
      const mainKey = `${movementType}_${shipmentType}`;

      if (!lookup[mainKey]) {
        lookup[mainKey] = {};
      }

      let secondKey = '';

      if (offsite) {
        secondKey += 'OFFSITE';
      }

      if (empty) {
        secondKey = secondKey === '' ? 'EMPTY' : secondKey + '_EMPTY';
      } else {
        secondKey = secondKey === '' ? 'LOADED' : secondKey + '_LOADED';
      }

      secondKey += secondKey === '' ? actionType : `_${actionType}`;

      lookup[mainKey] = {
        ...lookup[mainKey],
        [secondKey]: fetchUniqueDocuments
          ? _.uniq(stopDocuments.map((stopDocument) => stopDocument.document))
          : stopDocuments,
      };

      return lookup;
    }, {});
  } catch (e) {
    console.error(`Error getting shipper stop doc requirements: ${e.message}`);
    return {};
  }
};

// change in stops docs list to include carrier ones as well
export const getShipperStopDocumentRequirementsFromConfig = async (shipper) => {
  try {
    const { data } = await getStopDocumentsConfig(shipper);

    return (data?.getStopDocumentsConfigQuery?.stopConfigurations || []).reduce(
      (lookup, currentStopConfig) => {
        const {
          actionType,
          empty,
          movementType,
          offsite,
          shipmentType,
          stopDocuments,
        } = currentStopConfig;
        const mainKey = `${movementType}_${shipmentType}`;

        if (!lookup[mainKey]) {
          lookup[mainKey] = {};
        }

        let secondKey = '';

        if (offsite) {
          secondKey += 'OFFSITE';
        }

        if (empty) {
          secondKey = secondKey === '' ? 'EMPTY' : secondKey + '_EMPTY';
        } else {
          secondKey = secondKey === '' ? 'LOADED' : secondKey + '_LOADED';
        }

        secondKey += secondKey === '' ? actionType : `_${actionType}`;

        lookup[mainKey] = {
          ...lookup[mainKey],
          [secondKey]: stopDocuments,
        };

        return lookup;
      },
      {},
    );
  } catch (e) {
    console.error(`Error getting shipper stop doc requirements: ${e.message}`);
    return {};
  }
};

/**
 * Builds the main keys for document requirements lookup
 * @param {Object} shipment - The shipment object
 * @param {boolean} isCarrierMandatoryStopDocumentsEnabled - Feature flag state
 * @returns {Array<string>} Array of main keys
 */
const _getMainKeys = (shipment, isCarrierMandatoryStopDocumentsEnabled) => {
  const { shipment_type, order_type, order_movement_type } = shipment;

  let mainKeys = [`${order_type}_${shipment_type}`.toUpperCase()];
  if (isCarrierMandatoryStopDocumentsEnabled && order_type === 'domestic') {
    mainKeys.push(
      `${order_type}_${order_movement_type}_${shipment_type}`.toUpperCase(),
    );
  }

  return mainKeys;
};

/**
 * Builds the secondary key for document requirements lookup
 * @param {Object} stop - The stop object
 * @returns {string} The secondary key
 */
const _getSecondaryKeys = (stop) => {
  const { action, empty, off_site } = stop;
  let secondKey = '';

  if (off_site) {
    secondKey += 'OFFSITE';
  }

  if (empty) {
    secondKey = secondKey === '' ? 'EMPTY' : secondKey + '_EMPTY';
  } else {
    secondKey = secondKey === '' ? 'LOADED' : secondKey + '_LOADED';
  }

  secondKey += `_${action.toUpperCase()}`;

  return secondKey;
};

export const getOrderMovementStopDocumentRequirements = (
  shipment,
  stop,
  requiredDocuments,
) => {
  try {
    const isCarrierMandatoryStopDocumentsEnabled = featureEnabled(
      CONFIGURED_FEATURES.CARRIER_MANDATORY_STOP_DOCUMENTS,
    );

    const mainKeys = _getMainKeys(
      shipment,
      isCarrierMandatoryStopDocumentsEnabled,
    );
    const secondKey = _getSecondaryKeys(stop);

    const results = [];
    mainKeys.forEach((key) => {
      const shipmentDocumentConfigurations = requiredDocuments[key] || {};
      const docs = shipmentDocumentConfigurations[secondKey] || [];
      if (docs && docs.length) {
        results.push(...docs);
      }
    });

    return _.uniq(results);
  } catch (e) {
    console.error(`Error getting stop doc requirements: ${e.message}`);
    return [];
  }
};

const isPrimaryStopForLiveServiceType = (currentStop, job) => {
  return (
    currentStop?.action === 'pickup' &&
    job?.order?.service_type === 'live' &&
    ((job?.order_type === 'import' && !currentStop?.empty) ||
      (job?.order_type === 'export' && currentStop?.empty))
  );
};

const isPrimaryStopForRepositionOrderType = (currentStop, job) => {
  return job?.order_type === 'reposition' && currentStop?.sequence === 1;
};

const isPrimaryStopForDropAndHookType = (currentStop, job) => {
  if (job?.order?.service_type === 'drop') {
    return (
      (job?.order_type === 'export' || job?.order_type === 'import') &&
      currentStop?.action === 'pickup' &&
      !currentStop?.empty &&
      !currentStop?.off_site
    );
  }
  return false;
};

export const isPrimaryStopForShipment = (stop, job) => {
  return (
    job?.order?.order_type !== 'domestic' &&
    !stop?.off_site &&
    (isPrimaryStopForLiveServiceType(stop, job) ||
      isPrimaryStopForDropAndHookType(stop, job) ||
      isPrimaryStopForRepositionOrderType(stop, job))
  );
};

export const isStopCancelled = (stop) => {
  return stop.status === STOP_STATUS.CANCELED || stop.canceled === true;
};
